import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import TenantPicker from "@/super_admin/TenantPicker";
import RunOutcomeList from "./RunOutcomeList";
import { automationErrorMessage } from "./automationErrors";
import { useSecondaryRead } from "../screenAccess";

/**
 * The rule's play button: an honest dry run (hq-customers-contract.md section 4.2). The
 * operator may pick a practice to simulate against; the server evaluates the rule's
 * conditions and reports what each action would do, and changes nothing. A modal, so focus
 * moves in, Escape closes it and focus returns to the play button that opened it.
 *
 * The practice picker reads `GET /tenants/options`, which is super admin only; an operator
 * who may run the dry run (settings.write, through an override) but not read practices gets a
 * note instead, and the run goes without a practice.
 */
export default function DryRunDialog({ rule, onClose, onDone }) {
  const t = useT("superadmin");
  const canPickPractice = useSecondaryRead("tenantOptions");
  const [tenantId, setTenantId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  // The dialog is opened by state, not by a Radix Trigger, so Radix has no element to give
  // focus back to on close. The opener is whatever held focus when the dialog first rendered
  // (the play button, before focus moved in).
  const opener = useRef(typeof document === "undefined" ? null : document.activeElement);

  const run = async () => {
    setBusy(true); setError(""); setResult(null);
    try {
      const body = tenantId ? { tenant_id: tenantId } : {};
      const r = await api.post(`/super-admin/hq/automation/rules/${rule.rule_id}/test-run`, body);
      setResult(r.data);
      onDone?.();
    } catch (err) {
      setError(automationErrorMessage(err, t));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        // The shared DialogContent paints `bg-background`, the product theme's tinted surface;
        // the console's panels are white, so this dialog says so locally.
        className="max-w-lg bg-white border-slate-200 text-slate-800"
        data-testid="dry-run-dialog"
        onCloseAutoFocus={(e) => { e.preventDefault(); opener.current?.focus?.(); }}
      >
        <DialogHeader>
          <DialogTitle>{t("hauto.dryRun.title", { rule: rule.name })}</DialogTitle>
          <DialogDescription>{t("hauto.dryRun.hint")}</DialogDescription>
        </DialogHeader>

        {canPickPractice ? (
          <div>
            <div className="text-xs text-slate-600 mb-1">{t("hauto.dryRun.practice")}</div>
            <TenantPicker
              value={tenantId}
              onChange={(id) => { setTenantId(id); setResult(null); setError(""); }}
              allowAll
              allLabel={t("hauto.dryRun.noPractice")}
              placeholder={t("hauto.dryRun.searchPractice")}
              ariaLabel={t("hauto.dryRun.practice")}
              testId="dry-run-practice"
            />
          </div>
        ) : (
          <p className="text-xs text-slate-600" data-testid="dry-run-no-practice-note">{t("hauto.dryRun.practiceNeedsSuperAdmin")}</p>
        )}

        {error && <p role="alert" className="text-sm text-rose-600" data-testid="dry-run-error">{error}</p>}

        {result && (
          <div className="space-y-3" data-testid="dry-run-result" aria-live="polite">
            <p className={`text-sm font-medium ${result.conditions_matched ? "text-emerald-700" : "text-amber-700"}`} data-testid="dry-run-matched">
              {result.conditions_matched ? t("hauto.dryRun.matched") : t("hauto.dryRun.notMatched")}
            </p>
            <RunOutcomeList results={result.action_results} dry testId="dry-run-outcomes" />
            <p className="text-xs text-slate-500">{t("hauto.dryRun.nothingChanged")}</p>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose} data-testid="dry-run-close">{t("hauto.dryRun.close")}</Button>
          <Button type="button" onClick={run} disabled={busy} className="bg-sol-gold text-black hover:bg-amber-200" data-testid="dry-run-submit">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t("hauto.dryRun.run")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
